import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { list, request } from "./client";

/**
 * 3D xarita ma'lumotlari.
 *
 *   - reporting `/map/scene` — butun omborning kataklari (ixcham satr);
 *   - task `/work-queues/positions` — xodimlarning oxirgi joyli amali;
 *   - task `/tasks/{id}` — tanlangan xodimning joriy topshirig'i;
 *   - inventory `/license-plates` — katakdagi LP va nazoratdagi palletlar;
 *   - picking `/picking/orders` — pallet kimning buyurtmasi.
 *
 * Geometriya serverdan KELMAYDI: u koddan `modules/map3d/geometry.ts`
 * da quriladi (`wmskit/whmap` bilan bir sozlama).
 */

export interface SceneCell {
  location_id: string;
  code: string;
  zone_code: string;
  location_function: string;
  cell_type: string;
  capacity_pallets: number | null;
  occupied_slots: number;
  distinct_sku_count: number;
  on_hand_qty: string;
  is_active: boolean;
  blocked: boolean;
  sku_abc_class: string;
}

interface SceneResponse {
  columns: string[];
  rows: unknown[][];
  truncated: boolean;
}

/** Ixcham satrlarni obyektga aylantiradi — ustun NOMI bo'yicha, tartibga ishonmasdan. */
function decodeScene(response: SceneResponse): { cells: SceneCell[]; truncated: boolean } {
  const at = new Map(response.columns.map((name, index) => [name, index]));
  const get = (row: unknown[], name: string) => row[at.get(name) ?? -1];
  const cells = response.rows.map((row) => ({
    location_id: String(get(row, "location_id") ?? ""),
    code: String(get(row, "code") ?? ""),
    zone_code: String(get(row, "zone_code") ?? ""),
    location_function: String(get(row, "location_function") ?? ""),
    cell_type: String(get(row, "cell_type") ?? ""),
    capacity_pallets: (get(row, "capacity_pallets") as number | null) ?? null,
    occupied_slots: Number(get(row, "occupied_slots") ?? 0),
    distinct_sku_count: Number(get(row, "distinct_sku_count") ?? 0),
    on_hand_qty: String(get(row, "on_hand_qty") ?? "0"),
    is_active: Boolean(get(row, "is_active")),
    blocked: Boolean(get(row, "blocked")),
    sku_abc_class: String(get(row, "sku_abc_class") ?? ""),
  }));
  return { cells, truncated: response.truncated };
}

export function useMapScene() {
  return useQuery({
    queryKey: ["map3d", "scene"],
    // Bandlik daqiqa ichida kam o'zgaradi; xodimlar alohida va tez.
    refetchInterval: 60_000,
    queryFn: async ({ signal }) =>
      decodeScene(await request<SceneResponse>("reporting", "/api/v1/map/scene", { signal })),
  });
}

export interface PositionStep {
  at: string;
  step: string;
  task_id: string;
  task_type: string;
  task_status: string;
  document_number?: string;
  scanned_code?: string;
  location_id?: string;
  zone_code?: string;
  sku_id?: string;
}

export interface TaskLine {
  id: string;
  line_number: number;
  sku_id: string;
  planned_qty: string;
  actual_qty?: string;
  source_location_id?: string;
  target_location_id?: string;
  source_lp_id?: string;
  status: string;
  discrepancy_reason?: string;
  confirmed_at?: string;
}

export interface TaskDetail {
  id: string;
  task_type: string;
  status: string;
  warehouse_code?: string;
  source_zone_code?: string;
  target_zone_code?: string;
  required_role?: string;
  document_number?: string;
  order_id?: string;
  started_at?: string;
  created_at: string;
  lines?: TaskLine[];
}

export interface WorkerPosition {
  user_id: string;
  username: string;
  full_name?: string;
  roles: string[];
  kind: "forklift" | "person";
  busy: boolean;
  current: TaskDetail | null;
  last: PositionStep | null;
  last_activity_at?: string;
  last_action?: string;
}

/**
 * Xodimlar joyi — har 5 soniyada.
 *
 * SSE oqimi (`/stream`) faqat hisobot o'zgarishlari haqida xabar beradi
 * va xodim qadamini o'z ichiga olmaydi; shuning uchun qisqa so'rov.
 */
export function useWorkerPositions(enabled: boolean) {
  return useQuery({
    queryKey: ["map3d", "positions"],
    enabled,
    refetchInterval: 5_000,
    retry: false,
    queryFn: ({ signal }) =>
      list<WorkerPosition>("task", "/api/v1/work-queues/positions", { hours: 12 }, signal),
  });
}

export function useTaskDetail(taskId: string | null | undefined) {
  return useQuery({
    queryKey: ["map3d", "task", taskId ?? ""],
    enabled: Boolean(taskId),
    refetchInterval: 5_000,
    queryFn: ({ signal }) => request<TaskDetail>("task", `/api/v1/tasks/${taskId}`, { signal }),
  });
}

export interface LpContent {
  sku_id: string;
  lot_ref?: string;
  qty: string;
}

export interface LicensePlate {
  id: string;
  lp_code: string;
  lp_type: string;
  status: string;
  current_location_id?: string;
  contents: LpContent[];
  has_stock: boolean;
  updated_at: string;
}

/** Katakdagi LP lar — katak bosilganda. */
export function useLocationLps(locationId: string | null | undefined) {
  return useQuery({
    queryKey: ["map3d", "lps", locationId ?? ""],
    enabled: Boolean(locationId),
    queryFn: ({ signal }) =>
      list<LicensePlate>("inventory", "/api/v1/license-plates", { location_id: locationId ?? "", limit: 50 }, signal),
  });
}

/**
 * Buyurtma palletlari: nazoratda va jo'natishda turgan LP lar.
 *
 * Ular katakdagi saqlash palletidan farqli o'laroq BUYURTMAGA tegishli —
 * xaritada «kimning zakasi va qaysi statusda» shu ro'yxatdan.
 */
export function useOrderPalletLps() {
  const control = useQuery({
    queryKey: ["map3d", "lps", "CONTROL"],
    refetchInterval: 30_000,
    queryFn: ({ signal }) =>
      list<LicensePlate>("inventory", "/api/v1/license-plates", { status: "CONTROL", limit: 500 }, signal),
  });
  const shipping = useQuery({
    queryKey: ["map3d", "lps", "SHIPPING"],
    refetchInterval: 30_000,
    queryFn: ({ signal }) =>
      list<LicensePlate>("inventory", "/api/v1/license-plates", { status: "SHIPPING", limit: 500 }, signal),
  });
  const items = useMemo(
    () => [...(control.data?.items ?? []), ...(shipping.data?.items ?? [])],
    [control.data, shipping.data],
  );
  return { items, isLoading: control.isLoading || shipping.isLoading };
}

export interface PickingTara {
  lp_id: string;
  lp_code: string;
  seq: number;
  current: boolean;
  delivered_location_id?: string;
  delivered_location_code?: string;
  delivered_at?: string;
}

export interface PickingOrder {
  id: string;
  order_number: string;
  customer_id?: string;
  customer_name?: string;
  status: string;
  lp_id?: string;
  lp_code?: string;
  taras?: PickingTara[];
  picked_at?: string;
  created_at: string;
}

/** Ochiq buyurtmalar — LP → buyurtma bog'lanishi uchun. */
export function useOpenOrders() {
  return useQuery({
    queryKey: ["map3d", "orders"],
    refetchInterval: 60_000,
    queryFn: ({ signal }) =>
      list<PickingOrder>("picking", "/api/v1/picking/orders", { only_open: true, limit: 200 }, signal),
  });
}

export function useOrderDetail(orderId: string | null | undefined) {
  return useQuery({
    queryKey: ["map3d", "order", orderId ?? ""],
    enabled: Boolean(orderId),
    queryFn: ({ signal }) => request<PickingOrder>("picking", `/api/v1/picking/orders/${orderId}`, { signal }),
  });
}

interface SkuName {
  id: string;
  article: string;
  name: string;
  vendor_article?: string;
}

/** SKU nomlari BITTA so'rovda (master-data `ids`). */
export function useSkuNames(skuIds: Array<string | undefined>) {
  const key = [...new Set(skuIds.filter((id): id is string => Boolean(id)))].sort().join(",");
  const query = useQuery({
    queryKey: ["map3d", "skus", key],
    enabled: key !== "",
    queryFn: ({ signal }) => list<SkuName>("masterData", "/api/v1/skus", { ids: key, limit: 200 }, signal),
  });
  return useMemo(
    () => new Map((query.data?.items ?? []).map((sku) => [sku.id, sku] as const)),
    [query.data],
  );
}
